import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { API_BASE_URL } from '../config';
import '../styles/Overview.css';

const Overview = ({ userId }) => {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState({
    active: false,
    type: null,
    secondsRemaining: 0,
    totalSeconds: 0,
    label: 'Waiting...',
    endTime: null,
    showSoon: false,
    showInProgress: false
  });

  const [partyPhase, setPartyPhase] = useState('not_started');
  const [nextPhase, setNextPhase] = useState('');
  const [marketState, setMarketState] = useState({ isOpen: false });
  const [tradingBlock, setTradingBlock] = useState(1);
  const [currentMacroGame, setCurrentMacroGame] = useState(null);
  const [leaderboardTop, setLeaderboardTop] = useState([]);
  const [stats, setStats] = useState({ totalPlayers: 0, totalTrades: 0 });

  // Format seconds to MM:SS or H:MM:SS
  const formatCountdown = (seconds) => {
    if (seconds >= 3600) {
      const hours = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Trading session names by block (themed after global markets)
  const getTradingSessionName = (block) => {
    const sessions = {
      1: 'Asian Session',
      2: 'European Session',
      3: 'Canadian Session',
      4: 'Power Hour'  // 5-min final trading before closing bell
    };
    return sessions[block] || 'Trading';
  };

  // Game names by trading block (macro games after each trading session)
  const getGameName = (block) => {
    const games = {
      1: 'Portfolio Warfare',
      2: 'Dead Cat Bounce',
      3: 'A Brief Case'
      // Block 4 has no macro game - goes straight to closing bell
    };
    return games[block] || 'Macro Game';
  };

  // Phase labels
  const getPhaseLabel = (phase, block = 1, macroGameName = null) => {
    const labels = {
      'not_started': 'Ready to Start',
      'registration': 'Registration Open',
      'premarket': 'Pre-Market',
      'orientation': 'Orientation',
      'trading': getTradingSessionName(block),
      'macro_game': macroGameName || getGameName(block) || 'Macro Game',
      'power_hour': 'Power Hour',
      'closing': 'Closing Bell',
      'liquidity_crisis': 'Liquidity Crisis',
      'results': 'Results',
      'winners': 'Winners',
      'ended': 'Party Ended'
    };
    return labels[phase] || phase;
  };

  // Next phase labels
  const getNextPhaseLabel = (phase, block = 1) => {
    const nextMap = {
      'not_started': 'Registration + Premarket',
      'registration': 'Orientation',
      'premarket': 'Orientation',
      'orientation': 'Opening Bell',
      'trading': block >= 4 ? 'Closing Bell' : getGameName(block),
      'macro_game': block >= 3 ? 'Power Hour' : getTradingSessionName(block + 1),
      'power_hour': 'Closing Bell',
      'closing': 'Winners / Liquidity Crisis',
      'liquidity_crisis': 'Winners',
      'winners': 'Party Complete!',
      'results': 'Party Complete!',
      'ended': 'Party Complete!'
    };
    return nextMap[phase] || '';
  };

  // Fetch initial state
  useEffect(() => {
    const fetchState = async () => {
      try {
        let hasActiveCountdown = false;
        let currentPhase = 'not_started';
        let currentBlock = 1;
        let currentMacroGameName = null;

        // These phases are "in progress" - they show ACTIVE, not a countdown
        const inProgressPhases = ['orientation', 'macro_game', 'power_hour', 'liquidity_crisis', 'winners', 'results'];
        // These phases are "waiting" for the next transition
        const waitingPhases = ['premarket', 'closing'];

        // FIRST: Get party state to know current phase
        const partyRes = await fetch(`${API_BASE_URL}/api/party-state`);
        if (partyRes.ok) {
          const data = await partyRes.json();
          if (data.partyState) {
            currentPhase = data.partyState.phase || 'not_started';
            currentBlock = data.partyState.tradingBlockNumber || 1;
            currentMacroGameName = data.partyState.currentMacroGame || null;
            setPartyPhase(currentPhase);
            setTradingBlock(currentBlock);
            setCurrentMacroGame(currentMacroGameName);
            setNextPhase(getNextPhaseLabel(currentPhase, currentBlock));
          }
          if (data.marketState) {
            setMarketState(data.marketState);
          }
        }

        // If we're in an "in progress" phase, show ACTIVE and skip countdown fetches
        if (inProgressPhases.includes(currentPhase)) {
          setCountdown({
            active: false,
            type: currentPhase,
            secondsRemaining: 0,
            totalSeconds: 0,
            label: getPhaseLabel(currentPhase, currentBlock, currentMacroGameName),
            endTime: null,
            showSoon: false,
            showInProgress: true
          });
          hasActiveCountdown = true; // Skip other countdown fetches
        }

        // Get market countdown state (HARD countdown - Opening/Closing Bell)
        if (!hasActiveCountdown) {
          const countdownRes = await fetch(`${API_BASE_URL}/api/market-countdown`);
          if (countdownRes.ok) {
            const data = await countdownRes.json();
            if (data.active) {
              hasActiveCountdown = true;
              setCountdown({
                active: true,
                type: data.countdownType || 'market-open',
                secondsRemaining: data.secondsRemaining,
                totalSeconds: data.totalSeconds,
                label: data.countdownType === 'market-open' ? 'Opening Bell' :
                       data.countdownType === 'market-close' ? 'Closing Bell' : 'Countdown',
                endTime: Date.now() + (data.secondsRemaining * 1000)
              });
            }
          }
        }

        // Get trading block countdown
        if (!hasActiveCountdown) {
          const tradingRes = await fetch(`${API_BASE_URL}/api/trading-block-countdown`);
          if (tradingRes.ok) {
            const data = await tradingRes.json();
            if (data.active) {
              hasActiveCountdown = true;
              setTradingBlock(data.blockNumber);
              setCountdown({
                active: true,
                type: 'trading-block',
                secondsRemaining: data.secondsRemaining,
                totalSeconds: data.totalSeconds,
                label: getTradingSessionName(data.blockNumber),
                endTime: Date.now() + (data.secondsRemaining * 1000)
              });
            }
          }
        }

        // Get premarket countdown (soft countdown during pre-market phase)
        if (!hasActiveCountdown) {
          const premarketRes = await fetch(`${API_BASE_URL}/api/premarket-countdown`);
          if (premarketRes.ok) {
            const data = await premarketRes.json();
            if (data.active && data.secondsRemaining > 0) {
              hasActiveCountdown = true;
              setCountdown({
                active: true,
                type: 'premarket',
                secondsRemaining: data.secondsRemaining,
                totalSeconds: data.totalSeconds,
                label: 'Pre-Market',
                endTime: Date.now() + (data.secondsRemaining * 1000)
              });
              setNextPhase('Orientation');
            }
          }
        }

        // If still no countdown, show waiting state
        if (!hasActiveCountdown) {
          setCountdown(prev => ({
            ...prev,
            active: false,
            type: currentPhase,
            label: getPhaseLabel(currentPhase, currentBlock, currentMacroGameName),
            showInProgress: false,
            showSoon: waitingPhases.includes(currentPhase)
          }));
        }

        // Get leaderboard top 3
        const leaderboardRes = await fetch(`${API_BASE_URL}/api/leaderboard`);
        if (leaderboardRes.ok) {
          const data = await leaderboardRes.json();
          setLeaderboardTop(data.slice(0, 3));
          setStats(prev => ({ ...prev, totalPlayers: data.length }));
        }

        // Get market status (source of truth - fetch last)
        const marketRes = await fetch(`${API_BASE_URL}/api/market/status`);
        if (marketRes.ok) {
          const data = await marketRes.json();
          // API returns { marketState: {...} }
          if (data.marketState) {
            setMarketState(data.marketState);
          }
        }

      } catch (error) {
        console.error('Error fetching state:', error);
      }
    };

    fetchState();
    const interval = setInterval(fetchState, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, []);

  // Socket connection ref
  const socketRef = useRef(null);

  // Socket listeners for real-time updates
  useEffect(() => {
    // Create own socket connection (public, no auth required)
    socketRef.current = io(API_BASE_URL, {
      auth: { public: true }
    });

    const socket = socketRef.current;

    socket.on('countdown-start', (data) => {
      setCountdown({
        active: true,
        type: data.countdownType || 'market-open',
        secondsRemaining: data.seconds,
        totalSeconds: data.totalSeconds || data.seconds,
        label: data.countdownType === 'market-open' ? 'Opening Bell' :
               data.countdownType === 'market-close' ? 'Closing Bell' : 'Countdown',
        endTime: data.endTime || Date.now() + (data.seconds * 1000)
      });
    });

    socket.on('countdown-update', (data) => {
      setCountdown(prev => ({
        ...prev,
        secondsRemaining: data.seconds,
        endTime: data.endTime
      }));
    });

    socket.on('market-bell', (data) => {
      if (data.type === 'OPENING_BELL') {
        setMarketState(prev => ({ ...prev, isOpen: true }));
        setCountdown(prev => ({ ...prev, active: false }));
      } else if (data.type === 'CLOSING_BELL') {
        setMarketState(prev => ({ ...prev, isOpen: false }));
        setCountdown(prev => ({ ...prev, active: false }));
      }
    });

    socket.on('market-state-update', (data) => {
      if (data.marketState) {
        setMarketState(data.marketState);
      } else if (data.isOpen !== undefined) {
        setMarketState(prev => ({ ...prev, isOpen: data.isOpen }));
      }
    });

    socket.on('party-state-update', (data) => {
      if (data.partyState) {
        const phase = data.partyState.phase || 'not_started';
        const block = data.partyState.tradingBlockNumber || 1;
        const macroGame = data.partyState.currentMacroGame || null;
        setPartyPhase(phase);
        setTradingBlock(block);
        setCurrentMacroGame(macroGame);
        setNextPhase(getNextPhaseLabel(phase, block));

        // Update display state if no active countdown
        setCountdown(prev => {
          if (!prev.active) {
            const inProgressPhases = ['orientation', 'macro_game', 'power_hour', 'liquidity_crisis', 'winners', 'results'];
            const waitingPhases = ['premarket', 'closing'];
            return {
              ...prev,
              type: phase,
              label: getPhaseLabel(phase, block, macroGame),
              showInProgress: inProgressPhases.includes(phase),
              showSoon: waitingPhases.includes(phase)
            };
          }
          return prev;
        });
      }
      if (data.marketState) {
        setMarketState(data.marketState);
      }
    });

    socket.on('trading-block-countdown-start', (data) => {
      setTradingBlock(data.blockNumber);
      setCountdown({
        active: true,
        type: 'trading-block',
        secondsRemaining: data.seconds,
        totalSeconds: data.totalSeconds || data.seconds,
        label: data.sessionName || getTradingSessionName(data.blockNumber),
        endTime: data.endTime
      });
    });

    socket.on('close-countdown-start', (data) => {
      setCountdown({
        active: true,
        type: 'market-close',
        secondsRemaining: data.seconds,
        totalSeconds: data.totalSeconds || data.seconds,
        label: 'Closing Bell',
        endTime: data.endTime || (Date.now() + data.seconds * 1000)
      });
    });

    socket.on('close-countdown-tick', (data) => {
      setCountdown(prev => ({
        ...prev,
        secondsRemaining: data.seconds,
        endTime: data.endTime
      }));
    });

    socket.on('premarket-start', (data) => {
      const totalSeconds = (data.minutes || 60) * 60;
      setCountdown({
        active: true,
        type: 'premarket',
        secondsRemaining: totalSeconds,
        totalSeconds: totalSeconds,
        label: 'Pre-Market',
        endTime: data.endTime || (Date.now() + totalSeconds * 1000)
      });
      setPartyPhase('premarket');
      setNextPhase('Orientation');
    });

    socket.on('orientation-start', () => {
      setPartyPhase('orientation');
      setCountdown({
        active: false,
        type: 'orientation',
        secondsRemaining: 0,
        totalSeconds: 0,
        label: 'Orientation',
        endTime: null,
        showSoon: false,
        showInProgress: true
      });
      setNextPhase('Opening Bell');
    });

    socket.on('orientation-end', () => {
      setCountdown(prev => ({
        ...prev,
        label: 'Get Ready',
        showInProgress: false,
        showSoon: true
      }));
      setNextPhase('Market Open');
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  // Countdown tick effect
  useEffect(() => {
    if (!countdown.active || !countdown.endTime) return;

    const tick = () => {
      const now = Date.now();
      const remaining = Math.max(0, Math.floor((countdown.endTime - now) / 1000));

      if (remaining <= 0) {
        setCountdown(prev => ({
          ...prev,
          active: false,
          secondsRemaining: 0,
          showSoon: true
        }));
      } else {
        setCountdown(prev => ({
          ...prev,
          secondsRemaining: remaining
        }));
      }
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [countdown.active, countdown.endTime]);

  // Get countdown color based on type
  const getCountdownColor = () => {
    switch (countdown.type) {
      case 'market-open': return '#00ff88';
      case 'market-close': return '#ff4444';
      case 'macro-game': return '#a855f7';
      case 'trading-block': return '#00bcd4';
      case 'premarket': return '#00bcd4'; // Cyan for premarket
      default: return '#00ff41';
    }
  };

  return (
    <div className="overview-page">
      {/* Main Countdown Section */}
      <div className="countdown-section">
        <div className="countdown-label">
          {countdown.active ? countdown.label : countdown.label || getPhaseLabel(partyPhase, tradingBlock, currentMacroGame)}
        </div>
        <div
          className={`countdown-time ${countdown.active || countdown.showInProgress || (!countdown.active && partyPhase === 'trading') ? 'active' : ''}`}
          style={{
            color: countdown.active ? getCountdownColor() :
                   countdown.showInProgress ? '#8b5cf6' :
                   (!countdown.active && partyPhase === 'trading') ? '#22c55e' :
                   countdown.showSoon ? '#fbbf24' : undefined
          }}
        >
          {countdown.active ? formatCountdown(countdown.secondsRemaining) :
           countdown.showInProgress ? 'ACTIVE' :
           (!countdown.active && partyPhase === 'trading') ? 'ACTIVE' :
           countdown.showSoon ? 'SOON' : '--:--'}
        </div>
        {countdown.active && countdown.totalSeconds > 0 && (
          <div className="countdown-progress">
            <div
              className="countdown-progress-bar"
              style={{
                width: `${(countdown.secondsRemaining / countdown.totalSeconds) * 100}%`,
                background: getCountdownColor()
              }}
            />
          </div>
        )}
        {nextPhase && (
          <div className="countdown-next">
            Next: <span className="next-value">{nextPhase}</span>
          </div>
        )}
      </div>

      {/* Status Cards */}
      <div className="status-grid">
        {/* Market Status */}
        <div
          className={`status-card ${marketState.isOpen ? 'positive' : 'negative'}`}
          onClick={() => navigate('/market')}
        >
          <div className="status-icon">{marketState.isOpen ? '🟢' : '🔴'}</div>
          <div className="status-label">Market</div>
          <div className="status-value">{marketState.isOpen ? 'OPEN' : 'CLOSED'}</div>
        </div>

        {/* Party Phase */}
        <div className="status-card">
          <div className="status-icon">🎉</div>
          <div className="status-label">Phase</div>
          <div className="status-value">{getPhaseLabel(partyPhase, tradingBlock, currentMacroGame)}</div>
        </div>

        {/* Trading Block */}
        <div className="status-card">
          <div className="status-icon">📊</div>
          <div className="status-label">Session</div>
          <div className="status-value">{tradingBlock}/4</div>
        </div>

        {/* Players */}
        <div
          className="status-card"
          onClick={() => navigate('/leaderboard')}
        >
          <div className="status-icon">👥</div>
          <div className="status-label">Players</div>
          <div className="status-value">{stats.totalPlayers}</div>
        </div>
      </div>

      {/* Top 3 Leaderboard */}
      {leaderboardTop.length > 0 && (
        <div className="mini-leaderboard">
          <h3 onClick={() => navigate('/leaderboard')}>Top Traders</h3>
          <div className="leaderboard-rows">
            {leaderboardTop.map((trader, index) => (
              <div
                key={trader.id}
                className="leaderboard-row"
                onClick={() => navigate(`/portfolio/${trader.id}`)}
              >
                <span className="rank">{index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}</span>
                <span className="name">{trader.username || trader.displayName}</span>
                <span className={`gain ${trader.percentGain >= 0 ? 'positive' : 'negative'}`}>
                  {trader.percentGain >= 0 ? '+' : ''}{trader.percentGain?.toFixed(1)}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="quick-actions">
        <button onClick={() => navigate('/market')} className="action-btn market">
          📈 Market
        </button>
        <button onClick={() => navigate('/portfolio')} className="action-btn portfolio">
          💼 Portfolio
        </button>
        <button onClick={() => navigate('/leaderboard')} className="action-btn leaderboard">
          🏆 Leaderboard
        </button>
      </div>
    </div>
  );
};

export default Overview;
